import { motion } from 'framer-motion';
import React from 'react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { SEOHead } from '@components/SEOHead';

import { Hero } from '@ui/Hero';

import { COLORS } from '@constants/colors';

interface ToolLayoutProps {
  title: string;
  description: string;
  canonicalUrl: string;
  children: React.ReactNode;
  backgroundColor?: string;
  heroStyle?: 'simple' | 'gradient';
  heroIcon?: React.ReactNode;
  showBreadcrumb?: boolean;
  keywords?: string;
  heroTitle?: string; // Optional separate title for hero display
  cta?: React.ReactNode; // Optional CTA section outside content wrapper
}

export const ToolLayout: React.FC<ToolLayoutProps> = ({
  title,
  description,
  canonicalUrl,
  children,
  backgroundColor = COLORS.pearl,
  heroStyle = 'simple',
  heroIcon,
  showBreadcrumb = false,
  keywords,
  heroTitle,
  cta,
}) => {
  const breadcrumbItems = showBreadcrumb
    ? [
        { label: 'Home', href: '/' },
        { label: 'Tools', href: '/tools' },
        { label: heroTitle || title },
      ]
    : [];

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        keywords={keywords}
      />
      <div className="min-h-screen" style={{ backgroundColor }}>
        <Header showBackButton={false} />

        <Hero
          title={heroTitle || title}
          description={description}
          icon={heroIcon}
          showBreadcrumb={showBreadcrumb}
          breadcrumbItems={breadcrumbItems}
          variant={heroStyle}
        />

        {/* Content Section */}
        <motion.section
          className={heroStyle === 'gradient' ? 'py-8 sm:py-16' : ''}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: heroStyle === 'gradient' ? 0.4 : 0.2,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6">{children}</div>
        </motion.section>

        {/* CTA Section - Full Width */}
        {cta}

        <Footer />
      </div>
    </>
  );
};
